import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { RefCallBack } from 'react-hook-form'
import { FixedSizeList, ListChildComponentProps } from 'react-window'

import {
  Autocomplete,
  Button,
  Icon,
  Loader,
  Modal,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import {
  CustomerCreatedData,
  RenderBodyCustomerModal
} from 'templates/Customers/Modals/CustomerBodyModal'

import CanLogical from 'components/CanLogical'

import { formatCpfCnpj, formatPhone } from 'utils'

import { theme } from 'styles/theme'

type Record = {
  label: string
  value: string
  searchText: string
}

type CustomersAutocompleRecord = {
  name: string
  nickname?: string
  uuid: string
  document?: string
  phone?: string
  email?: string
}

type AutocompleteCustomersProps = {
  onChange: any
  value: any
  nameCustomer?: string
  errorMessage?: any
  multiple?: boolean
  placeholder?: string
  disabled?: boolean
  label?: string
  inputRef?: RefCallBack
  showAddButton?: boolean
  allowAllOption?: boolean
}

const OuterElementContext = React.createContext<
  React.HTMLAttributes<HTMLDivElement>
>({})

const OuterElement = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})
OuterElement.displayName = 'OuterElement'

const VirtualizedList = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; isLoading: boolean; match: boolean }
>(function ListboxComponent(props, ref) {
  const { children, isLoading, match, ...other } = props as {
    children: React.ReactNode
    isLoading?: boolean
    match: boolean
  }

  const itemCount = React.Children.count(children)
  const height = match ? 130 : 60

  const itemData = React.Children.toArray(children)

  const renderRow = ({ index, style }: ListChildComponentProps<any>) => {
    return (
      <div style={style}>
        {isLoading && index >= itemData.length ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}
          >
            <Loader />
          </div>
        ) : (
          itemData[index]
        )}
      </div>
    )
  }

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={600}
          width="100%"
          itemSize={height}
          outerElementType={OuterElement}
          innerElementType="ul"
          itemCount={isLoading ? itemCount + 3 : itemCount}
          overscanCount={5}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

const AutocompleteCustomers = ({
  inputRef,
  onChange,
  value,
  errorMessage,
  placeholder,
  label,
  nameCustomer,
  disabled = false,
  multiple = false,
  showAddButton = false,
  allowAllOption = false
}: AutocompleteCustomersProps) => {
  const [customers, setCustomers] = useState<Record[]>([])
  const [names, setNames] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const match = useMediaQuery('(max-width: 600px)')
  // O botão de criar cliente só aparece para quem tem customers:manage —
  // é a permissão que o backend exige em customers#create.
  const canAddCustomer =
    showAddButton && !!CanLogical({ permission: 'customers:manage' })

  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/

  const getAutocompleteCustomers = useCallback(async () => {
    setLoading(true)
    const responseCustumersAutocomplete = await DataService({
      type: 'GET',
      url: '/v1/customers_autocomplete'
    })

    if (responseCustumersAutocomplete.status === 200) {
      const customersList = responseCustumersAutocomplete.data.records.map(
        (record: CustomersAutocompleRecord) => ({
          label: record.name,
          value: record.uuid,
          searchText: [
            record.name,
            record.nickname,
            record.document,
            record.phone,
            record.email
          ].join(' | ')
        })
      )

      setCustomers(customersList)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (
      nameCustomer?.includes(';') ||
      uuidRegex.test(nameCustomer || '') ||
      !nameCustomer
    ) {
      getAutocompleteCustomers()
    }
  }, [])

  useEffect(() => {
    if (!nameCustomer) return

    let names = ''
    if (nameCustomer.includes(';')) {
      const ArrayUuid = nameCustomer.split(';')
      ArrayUuid.forEach((uuid: string, index: number) => {
        const customer = customers.find((customer) => customer.value === uuid)
        names = `${names}${customer?.label}${
          ArrayUuid.length !== index + 1 ? ', ' : ''
        }`
      })
    } else {
      if (uuidRegex.test(nameCustomer)) {
        const customer = customers.find(
          (customer) => customer.value === nameCustomer
        )
        names = customer?.label ? customer.label : nameCustomer
      } else {
        names = nameCustomer
      }
    }
    setNames(names)
  }, [customers])

  const handleCustomerCreated = async (customer: CustomerCreatedData) => {
    await getAutocompleteCustomers()

    const newCustomerOption: Record = {
      label: customer.name,
      value: customer.uuid,
      searchText: [
        customer.name,
        customer.document,
        customer.phone,
        customer.email
      ].join(' | ')
    }

    if (multiple) {
      const currentValue = Array.isArray(value) ? value : []
      onChange(null, [...currentValue, newCustomerOption])
    } else {
      onChange(null, newCustomerOption)
    }

    setModalOpen(false)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  const ADD_OPTION_VALUE = '__add_new_customer__'
  const ALL_OPTION_VALUE = '*'
  const ALL_OPTION: Record = {
    label: 'Todos os clientes',
    value: ALL_OPTION_VALUE,
    searchText: 'todos os clientes'
  }

  const optionsWithAddButton = [
    ...(canAddCustomer
      ? [{ label: '', value: ADD_OPTION_VALUE, searchText: '' }]
      : []),
    ...(allowAllOption ? [ALL_OPTION] : []),
    ...customers
  ]

  const AddCustomerButton = () => (
    <Stack width="100%" mt={1.2}>
      <Button
        fullWidth
        variantButton="ghost"
        sx={{ justifyContent: 'flex-start' }}
      >
        <Stack direction="row" gap="5px" alignItems="center">
          <Icon
            icon="add"
            sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
          />
          <Typography
            sx={{
              color: theme.palette.neutral[400],
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Adicionar cliente
          </Typography>
        </Stack>
      </Button>
    </Stack>
  )

  return nameCustomer ? (
    <Textfield
      inputRef={inputRef || null}
      name="customerUuid"
      error={!!errorMessage}
      helperText={errorMessage || ''}
      placeholder="Buscar cliente"
      value={!loading ? names : 'Carregando...'}
      disabled={!!names}
    />
  ) : (
    <>
      <Autocomplete
        id="customer_uuid"
        onChange={(event, newValue) => {
          const selectedOption = Array.isArray(newValue)
            ? newValue[newValue.length - 1]
            : newValue
          if ((selectedOption as Record)?.value === ADD_OPTION_VALUE) {
            setModalOpen(true)
            return
          }
          if (multiple && Array.isArray(newValue)) {
            const justSelectedAll =
              (selectedOption as Record)?.value === ALL_OPTION_VALUE
            if (justSelectedAll) {
              onChange(event, [ALL_OPTION])
              return
            }
            const filtered = newValue.filter(
              (item) => (item as Record).value !== ALL_OPTION_VALUE
            )
            onChange(event, filtered)
            return
          }
          onChange(event, newValue)
        }}
        value={value}
        options={optionsWithAddButton}
        ListboxComponent={(props) => (
          <VirtualizedList
            {...(props as any)}
            isLoading={loading}
            match={match}
          />
        )}
        disabled={disabled}
        autoHighlight
        multiple={multiple}
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter((option) => {
            const opt = option as Record
            if (opt.value === ADD_OPTION_VALUE) return true
            if (opt.value === ALL_OPTION_VALUE) return true
            return opt.searchText
              .toLowerCase()
              .includes(inputValue.toLowerCase())
          })
          return filtered
        }}
        getOptionLabel={(option) => {
          if (!option) return ''
          const opt = option as Record
          if (opt.value === ADD_OPTION_VALUE) return ''
          return opt.label
        }}
        isOptionEqualToValue={(option, value) =>
          (option as Record).value === (value as Record).value
        }
        renderOption={(props, option) => {
          const optionTyped = option as Record

          if (optionTyped.value === ADD_OPTION_VALUE) {
            return (
              <Box
                component="li"
                key="add-new-customer"
                {...props}
                onClick={(e) => {
                  e.stopPropagation()
                  setModalOpen(true)
                }}
                sx={{
                  width: '100%',
                  borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                  mt: '-8px'
                }}
              >
                <AddCustomerButton />
              </Box>
            )
          }

          if (optionTyped.value === ALL_OPTION_VALUE) {
            return (
              <Box
                component="li"
                key="all-customers"
                {...props}
                sx={{
                  width: '100%',
                  borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                  mt: '-8px'
                }}
              >
                <Stack width="100%" mt={1.2}>
                  <Button
                    fullWidth
                    variantButton="ghost"
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.neutral[400],
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      {optionTyped.label}
                    </Typography>
                  </Button>
                </Stack>
              </Box>
            )
          }

          const [name, nickname, document, phone, email] =
            optionTyped.searchText.split(' | ')

          return (
            <Stack
              component="li"
              gap={1}
              p={1}
              pb={1.5}
              {...props}
              sx={{
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: '#F2F5F2'
                },
                justifyContent: 'start !important',
                alignItems: 'start !important',
                flexWrap: 'nowrap'
              }}
              key={optionTyped.value}
            >
              <Stack
                direction={match ? 'column' : 'row'}
                gap={match ? -0.2 : 0.5}
                width="100%"
                alignItems={match ? 'start' : 'center'}
              >
                <Typography
                  color={theme.palette.neutral[200]}
                  fontWeight={600}
                  fontSize="15px"
                  whiteSpace="nowrap"
                >
                  {name}
                </Typography>
                {nickname && !match && (
                  <Typography fontSize="12px">&nbsp;-&nbsp;</Typography>
                )}
                {nickname && (
                  <Typography fontSize="12px" color="#000" whiteSpace="nowrap">
                    {nickname}
                  </Typography>
                )}
              </Stack>
              <Stack
                direction={match ? 'column' : 'row'}
                gap={0.3}
                color="#777877"
                sx={{ flexWrap: 'nowrap' }}
              >
                {[
                  { value: email, formatter: (s: string) => s },
                  { value: phone, formatter: (s: string) => formatPhone(s) },
                  {
                    value: document,
                    formatter: (s: string) => formatCpfCnpj(s)
                  }
                ]
                  .filter((item) => item.value)
                  .map((item, index, array) => (
                    <React.Fragment key={index}>
                      <Typography fontSize="12px" whiteSpace="nowrap">
                        {item.formatter(item.value)}
                      </Typography>
                      {index < array.length - 1 && !match && (
                        <Typography fontSize="12px">&nbsp;|&nbsp;</Typography>
                      )}
                    </React.Fragment>
                  ))}
              </Stack>
            </Stack>
          )
        }}
        noOptionsText={
          canAddCustomer ? (
            <Box
              onClick={() => setModalOpen(true)}
              sx={{ cursor: 'pointer', width: '100%' }}
            >
              <AddCustomerButton />
            </Box>
          ) : (
            'Nenhuma opção encontrada'
          )
        }
        renderInput={(params) => (
          <Textfield
            inputRef={inputRef || null}
            error={!!errorMessage}
            helperText={errorMessage || ''}
            {...(label ? { label } : {})}
            {...(placeholder ? { placeholder } : {})}
            sx={
              multiple
                ? {
                    '& .MuiInputBase-root': {
                      height: '100%',
                      minHeight: '42px'
                    }
                  }
                : undefined
            }
            {...params}
          />
        )}
      />

      {canAddCustomer &&
        modalOpen &&
        createPortal(
          <Modal
            handleClose={handleCloseModal}
            open={modalOpen}
            size="large"
            width="100%"
            maxWidth="80vw"
            maxHeight="90vh"
            height="auto"
          >
            <RenderBodyCustomerModal
              onClose={handleCloseModal}
              isFromAutocomplete
              onCustomerCreated={handleCustomerCreated}
            />
          </Modal>,
          document.body
        )}
    </>
  )
}

export default AutocompleteCustomers
